import React, { useState, useEffect, useRef } from 'react';
import '../assets/css/AdsAssistantChat.css';
import ReactMarkdown from 'react-markdown';
import apiClient from '../services/API';

const AdsAssistantChat = ({ context = 'general' }) => {
    const [history, setHistory] = useState([]);
    const [userInput, setUserInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [currentProfileId, setCurrentProfileId] = useState(null);
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [showHistorySidebar, setShowHistorySidebar] = useState(false);
    const chatMessagesRef = useRef(null);
    const MAX_CHAR_COUNT = 2000;

    // Load list of conversations for the current account
    const loadConversations = () => {
        const profileId = localStorage.getItem("selectedAccountValue");
        if (!profileId) return;

        const conversationsKey = `conversations_${profileId}_${context}`;
        const saved = localStorage.getItem(conversationsKey);

        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setConversations(Array.isArray(parsed) ? parsed : []);
            } catch (e) {
                console.error('Error loading conversations:', e);
                setConversations([]);
            }
        } else {
            setConversations([]);
        }
    };

    // Save list of conversations
    const saveConversations = (convList) => {
        const profileId = localStorage.getItem("selectedAccountValue");
        if (!profileId) return;

        const conversationsKey = `conversations_${profileId}_${context}`;
        localStorage.setItem(conversationsKey, JSON.stringify(convList));
    };

    // Load specific conversation
    const loadConversation = (conversationId) => {
        const profileId = localStorage.getItem("selectedAccountValue");
        if (!profileId) return;

        const historyKey = `chat_${profileId}_${context}_${conversationId}`;
        const saved = localStorage.getItem(historyKey);

        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setHistory(Array.isArray(parsed) ? parsed : []);
                setCurrentConversationId(conversationId);
            } catch (e) {
                console.error('Error loading conversation:', e);
                setHistory([]);
            }
        }
    };

    // Create new conversation
    const createNewConversation = () => {
        const profileId = localStorage.getItem("selectedAccountValue");
        if (!profileId) return;

        const newConvId = `conv_${Date.now()}`;
        const newConv = {
            id: newConvId,
            title: 'New conversation',
            createdAt: new Date().toISOString(),
            lastMessageAt: new Date().toISOString(),
            messageCount: 0
        };

        const updatedConversations = [newConv, ...conversations];
        setConversations(updatedConversations);
        saveConversations(updatedConversations);
        setHistory([]);
        setCurrentConversationId(newConvId);
    };

    // Update conversation title based on first message
    const updateConversationTitle = (convId, firstMessage) => {
        const updatedConversations = conversations.map(conv => {
            if (conv.id === convId && conv.title === 'New conversation') {
                return {
                    ...conv,
                    title: firstMessage.substring(0, 50) + (firstMessage.length > 50 ? '...' : ''),
                    lastMessageAt: new Date().toISOString(),
                    messageCount: 1
                };
            } else if (conv.id === convId) {
                return {
                    ...conv,
                    lastMessageAt: new Date().toISOString(),
                    messageCount: (conv.messageCount || 0) + 1
                };
            }
            return conv;
        });
        setConversations(updatedConversations);
        saveConversations(updatedConversations);
    };

    // Delete conversation
    const deleteConversation = (convId) => {
        if (!window.confirm('Are you sure you want to delete this conversation')) return;

        const profileId = localStorage.getItem("selectedAccountValue");
        if (!profileId) return;

        // Delete from localStorage
        const historyKey = `chat_${profileId}_${context}_${convId}`;
        localStorage.removeItem(historyKey);

        // Update list
        const updatedConversations = conversations.filter(c => c.id !== convId);
        setConversations(updatedConversations);
        saveConversations(updatedConversations);

        // If this is the current one, clear it
        if (currentConversationId === convId) {
            setHistory([]);
            setCurrentConversationId(null);
        }
    };

    // Clear current conversation history
    const handleClearHistory = () => {
        if (!currentConversationId) return;
        if (!window.confirm('Are you sure you want to clear this conversation history')) return;

        setHistory([]);

        // Update localStorage
        const profileId = localStorage.getItem("selectedAccountValue");
        if (profileId && currentConversationId) {
            const historyKey = `chat_${profileId}_${context}_${currentConversationId}`;
            localStorage.setItem(historyKey, JSON.stringify([]));
        }
    };

    // Load conversations when account changes
    useEffect(() => {
        const profileId = localStorage.getItem("selectedAccountValue");

        if (profileId && profileId !== currentProfileId) {
            setCurrentProfileId(profileId);
            loadConversations();

            // Load the latest conversation or create a new one
            const conversationsKey = `conversations_${profileId}_${context}`;
            const saved = localStorage.getItem(conversationsKey);

            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    if (parsed.length > 0) {
                        loadConversation(parsed[0].id);
                    } else {
                        createNewConversation();
                    }
                } catch (e) {
                    createNewConversation();
                }
            } else {
                createNewConversation();
            }
        }
    }, [currentProfileId, context]);

    // Save history when it changes
    useEffect(() => {
        if (currentProfileId && currentConversationId && history.length > 0) {
            const historyKey = `chat_${currentProfileId}_${context}_${currentConversationId}`;
            localStorage.setItem(historyKey, JSON.stringify(history));
        }
    }, [history, currentProfileId, currentConversationId, context]);

    useEffect(() => {
        if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
    }, [history, isLoading]);

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        const message = userInput.trim();
        if (!message || isLoading) return;

        const profileId = localStorage.getItem("selectedAccountValue");
        const accountName = localStorage.getItem("selectedAccountName");
        const llmModel = localStorage.getItem("selectedLlmModel");

        if (!profileId) {
            const errorResponse = {
                role: 'system',
                content: 'Error: Please select an Amazon account in the main menu before using the assistant.'
            };
            setHistory(prev => [...prev, errorResponse]);
            return;
        }

        console.log(`Sending message to account: ${accountName} (ID: ${profileId})`);
        // Update title if this is the first message
        if (history.length === 0 && currentConversationId) {
            updateConversationTitle(currentConversationId, message);
        }


        const newUserMessage = { role: 'user', content: message, timestamp: new Date().toISOString() };
        setHistory(prev => [...prev, newUserMessage]);
        setUserInput("");
        setIsLoading(true);

        try {
            const response = await apiClient.post('/ads-assistant/chat', {
                message: message,
                history: history.slice(-10),
                profileId: profileId,
                context: context,
                llmModel: llmModel || undefined,
            });

            const aiResponse = {
                role: 'assistant',
                content: response.data.response,
                timestamp: new Date().toISOString()
            };
            setHistory(prev => [...prev, aiResponse]);

        } catch (error) {
            console.error("Error contacting AI assistant:", error);
            const errorMessage = error.response.data.error || error.message;
            const errorResponse = {
                role: 'system',
                content: `Connection error: ${errorMessage}`
            };
            setHistory(prev => [...prev, errorResponse]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="chat-container-wrapper">
            {/* History sidebar */}
            <div className={`history-sidebar ${showHistorySidebar ? 'open' : 'closed'}`}>
                <div className="history-header">
                    <h6>Conversations</h6>
                    <button
                        className="btn-new-conversation"
                        onClick={createNewConversation}
                        title="New conversation"
                    >
                        <i className="bi bi-plus-lg"></i>
                    </button>
                </div>

                <div className="conversations-list">
                    {conversations.length === 0 ? (
                        <div className="no-conversations">
                            <i className="bi bi-chat-dots"></i>
                            <p>No conversations</p>
                        </div>
                    ) : (
                        conversations.map(conv => (
                            <div
                                key={conv.id}
                                className={`conversation-item ${currentConversationId === conv.id ? 'active' : ''}`}
                                onClick={() => loadConversation(conv.id)}
                            >
                                <div className="conversation-info">
                                    <span className="conversation-title">{conv.title}</span>
                                    <span className="conversation-date">{formatDate(conv.lastMessageAt)}</span>
                                </div>
                                <button
                                    className="btn-delete-conversation"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        deleteConversation(conv.id);
                                    }}
                                    title="Delete conversation"
                                >
                                    <i className="bi bi-trash"></i>
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Toggle sidebar button */}
            <button
                className="btn-toggle-history"
                onClick={() => setShowHistorySidebar(!showHistorySidebar)}
                title={showHistorySidebar ? 'Hide history' : 'Show history'}
            >
                <i className={`bi bi-${showHistorySidebar ? 'chevron-left' : 'clock-history'}`}></i>
            </button>

            <div className="chat-container-main">
                {/* Clear history button */}
                {history.length > 0 && (
                    <div className="chat-actions-bar">
                        <span className="conversation-title-display">
                            {conversations.find(c => c.id === currentConversationId)?.title || 'Conversation'}
                        </span>
                        <button
                            onClick={handleClearHistory}
                            className="btn-clear-history"
                            title="Clear conversation history"
                        >
                            <i className="bi bi-trash"></i> Clear history
                        </button>
                    </div>
                )}

                <div className="chat-messages" ref={chatMessagesRef}>
                    {(history && history.length === 0) ? (
                        <WelcomeScreen />
                    ) : (
                        (history && Array.isArray(history)) && history.map((msg, index) => (
                            <ChatMessage key={index} message={msg} />
                        ))
                    )}
                    {isLoading && <LoadingIndicator />}
                </div>

                <div className="chat-input-container">
                    <form onSubmit={handleSendMessage} className="chat-input-form">
                        <textarea
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            className="chat-input"
                            placeholder="Type your message here..."
                            rows="1"
                            maxLength={MAX_CHAR_COUNT}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage(e);
                                }
                            }}
                        />
                        <div className="chat-actions">
                            <button type="submit" className="btn-send" disabled={!userInput.trim() || isLoading}>
                                <i className="bi bi-send"></i>
                            </button>
                        </div>
                        <div className="char-count">{userInput.length}/{MAX_CHAR_COUNT}</div>
                    </form>
                </div>
            </div>
        </div>
    );
};

const WelcomeScreen = () => (
    <div className="welcome-container">
        <div className="robot-icon"><i className="bi bi-lightning-charge"></i></div>
        <h4 className="welcome-header">Welcome to Amazon Ads Assistant</h4>
        <div className="welcome-message">
            <p className="welcome-subtitle">How can I help you today</p>
            <div className="help-section">
                <h6>You can ask me about:</h6>
                <div className="help-item"><i className="bi bi-megaphone"></i><div>Create SPA campaigns (Single keyword campaigns)</div></div>
                <div className="help-item"><i className="bi bi-magic"></i><div>Create automatic campaigns with bid recommendations</div></div>
                <div className="help-item"><i className="bi bi-collection"></i><div>Generate keyword group campaigns</div></div>
                <div className="help-item"><i className="bi bi-box"></i><div>Create product campaigns and sponsored brands</div></div>
                <p className="help-question">Type for example: "create spa campaign" to get started.</p>
            </div>
        </div>
    </div>
);

const ChatMessage = ({ message }) => {
    const isUser = message.role === 'user';
    const isAssistant = message.role === 'assistant';
    const isSystem = message.role === 'system';

    return (
        <div className={`message-row ${isUser ? 'user-message' : isAssistant ? 'ai-message' : 'system-message'}`}>
            <div className="message-content">
                {isAssistant || isSystem ? (
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                ) : (
                    message.content
                )}
            </div>
        </div>
    );
};

const LoadingIndicator = () => (
    <div className="message-row ai-message">
        <div className="message-content">
            <div className="typing-indicator"><span></span><span></span><span></span></div>
        </div>
    </div>
);

export default AdsAssistantChat;

